/**
 * Terminology-overlay editor (ADR-126 Decision 3 / ADR-092 / ADR-051).
 *
 * The operating domain only knows OPERATING / NOT_OPERATING; this surface lets
 * a tenant rename those states into their own vocabulary ("In session" /
 * "Open" / "On watch", etc.) plus the noun for a scheduled event. One overlay
 * per scope, with org→site inheritance: a site with no own overlay shows (and
 * starts from) the effective inherited one.
 */

import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Center,
  Field,
  Flex,
  HStack,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  InputField,
  StatusBadge,
  type StatusType,
} from '@themes/aegix';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  operatingTerminologyFormSchema,
  type OperatingTerminologyFormValues,
} from '../../validations/whakahaereConfig.validation';
import {
  DEFAULT_TERMINOLOGY,
  TERMINOLOGY_PRESETS,
  type OperatingTerminology,
} from '../../services';
import {
  useTerminology,
  useTerminologyList,
  useEffectiveTerminology,
  useCreateTerminology,
  useUpdateTerminology,
} from '../../hooks/queries';
import type { ConfigScope, ScopeRow } from './types';
import { scopeOf, ownKeysFrom } from './types';
import { useScopeRows } from './useScopeRows';
import { ScopeGroupTable } from './ScopeGroupTable';
import { toaster } from '../ui/toaster';

/**
 * Terminology tab: a site-grouped table — "Organization default" +
 * one row per site — each expanding INLINE to that scope's 3-label form, rather
 * than gating behind a scope-selector dropdown. It's a singleton setting (one
 * overlay per scope), so there is nothing to "Add".
 */
export function TerminologyEditor() {
  const { t } = useTranslation();
  const { data: overlays = [], isLoading: overlaysLoading } =
    useTerminologyList({});
  const ownKeys = useMemo(() => ownKeysFrom(overlays), [overlays]);
  const { rows, isLoading: rowsLoading, error } = useScopeRows(ownKeys);

  const overlayFor = (row: ScopeRow) =>
    overlays.find((t) =>
      row.kind === 'org'
        ? t.school == null && t.district === row.districtId
        : t.school === row.schoolId,
    );

  return (
    <ScopeGroupTable
      rows={rows}
      isLoading={overlaysLoading || rowsLoading}
      error={error}
      searchPlaceholder={t('whakahaere.searchSites', 'Search sites')}
      summaryColumn={{
        key: 'labels',
        label: t('whakahaere.columns.labels', 'Labels'),
        sortable: false,
        render: (row) => {
          const item = overlayFor(row);
          return (
            <Text fontSize="sm" color="text.secondary">
              {item ? `${item.operating_label} / ${item.not_operating_label}` : '—'}
            </Text>
          );
        },
      }}
      renderExpandedRow={(row) => <TerminologyPanel scope={scopeOf(row)} />}
      e2eTitle="terminology-scopes"
    />
  );
}

interface TerminologyPanelProps {
  scope: ConfigScope;
}

const PRESET_LABELS: Record<string, string> = {
  k12: 'K-12 (In session)',
  corporate: 'Corporate (Open)',
  responder: 'Responder (On watch)',
};

function TerminologyPanel({ scope }: TerminologyPanelProps) {
  const { t } = useTranslation();
  const scopeFilter = useMemo(
    () =>
      scope.schoolId
        ? { school: scope.schoolId }
        : { district: scope.districtId },
    [scope],
  );

  // The overlay attached directly at this scope (may be null).
  const ownQuery = useTerminology(scopeFilter);
  // The effective overlay (inherited fallback) — seeds the form defaults.
  const effectiveQuery = useEffectiveTerminology(scopeFilter);

  const createTerminology = useCreateTerminology();
  const updateTerminology = useUpdateTerminology();

  const own = ownQuery.data ?? null;
  const effective = effectiveQuery.data ?? null;
  const isInherited = !own && !!effective;

  const seed = own ?? effective ?? null;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<OperatingTerminologyFormValues>({
    defaultValues: toForm(seed),
    resolver: zodResolver(operatingTerminologyFormSchema),
    mode: 'onBlur',
  });

  useEffect(() => {
    if (!ownQuery.isLoading && !effectiveQuery.isLoading) {
      reset(toForm(seed));
    }
  }, [seed, ownQuery.isLoading, effectiveQuery.isLoading, reset]);

  const applyPreset = (key: string) => {
    const preset = TERMINOLOGY_PRESETS[key];
    if (!preset) return;
    setValue('operating_label', preset.operating_label, { shouldDirty: true });
    setValue('not_operating_label', preset.not_operating_label, {
      shouldDirty: true,
    });
    setValue('event_label', preset.event_label ?? '', { shouldDirty: true });
  };

  const getPresetLabel = (key: string) => {
    if (key === 'k12') return t('whakahaere.presetK12', 'K-12 (In session)');
    if (key === 'corporate') return t('whakahaere.presetCorporate', 'Corporate (Open)');
    if (key === 'responder') return t('whakahaere.presetResponder', 'Responder (On watch)');
    return PRESET_LABELS[key] ?? key;
  };

  const onSubmit = async (values: OperatingTerminologyFormValues) => {
    const payload = {
      operating_label: values.operating_label,
      not_operating_label: values.not_operating_label,
      event_label: values.event_label ?? '',
    };
    try {
      if (own) {
        await updateTerminology.mutateAsync({ id: own.id, data: payload });
        toaster.create({ title: t('whakahaere.terminologyUpdated', 'Terminology updated'), type: 'success' });
      } else {
        // Creating an override (site) or the org default — bind to this scope.
        await createTerminology.mutateAsync({
          district: scope.schoolId ? null : scope.districtId,
          school: scope.schoolId ?? null,
          ...payload,
        });
        toaster.create({
          title: scope.schoolId
            ? t('whakahaere.siteTerminologyCreated', 'Site terminology created')
            : t('whakahaere.terminologyCreated', 'Terminology created'),
          type: 'success',
        });
      }
    } catch (err) {
      toaster.create({
        title: t('whakahaere.couldNotSaveTerminology', 'Could not save the terminology'),
        description: err instanceof Error ? err.message : undefined,
        type: 'error',
      });
    }
  };

  if (ownQuery.isLoading || effectiveQuery.isLoading) {
    return (
      <Center py={12}>
        <Spinner size="lg" color="button.primary" />
      </Center>
    );
  }

  return (
    <Box px={5} py={5} maxW="640px">
      <VStack align="stretch" gap={5}>
        <Box>
          <HStack gap={3} mb={1}>
            <Text fontWeight="600">{t('whakahaere.operatingTerminology', 'Operating terminology')}</Text>
            {isInherited && (
              <StatusBadge status={'info' as StatusType} label={t('whakahaere.statusInherited', 'Inherited')} />
            )}
          </HStack>
          <Text fontSize="sm" color="text.secondary">
            {t('whakahaere.operatingTerminologyDesc', 'Rename the operating states into your organization\'s vocabulary. These labels appear wherever the operating state is shown.')}
            {isInherited &&
              ` ${t('whakahaere.operatingTerminologyInheritedNotice', 'Saving here creates an override for this site; until then it inherits the organization default.')}`}
          </Text>
        </Box>

        {/* Vertical presets */}
        <Box>
          <Text fontSize="sm" fontWeight="500" mb={2} color="text.secondary">
            {t('whakahaere.startFromPreset', 'Start from a preset')}
          </Text>
          <Flex gap={2} wrap="wrap">
            {Object.keys(TERMINOLOGY_PRESETS).map((key) => (
              <ButtonComponent
                key={key}
                visual="secondary"
                type="button"
                onClick={() => applyPreset(key)}
                data-e2e={`terminology-preset-${key}`}
              >
                {getPresetLabel(key)}
              </ButtonComponent>
            ))}
          </Flex>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <VStack align="stretch" gap={4}>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
              <Field.Root invalid={!!errors.operating_label}>
                <Field.Label htmlFor="term-operating">
                  {t('whakahaere.operatingLabel', 'Operating label')}
                </Field.Label>
                <Controller
                  name="operating_label"
                  control={control}
                  render={({ field }) => (
                    <InputField
                      id="term-operating"
                      placeholder={DEFAULT_TERMINOLOGY.operating_label}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      data-e2e="terminology-operating-input"
                    />
                  )}
                />
                {errors.operating_label && (
                  <Field.ErrorText>
                    {errors.operating_label.message ? t(errors.operating_label.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>

              <Field.Root invalid={!!errors.not_operating_label}>
                <Field.Label htmlFor="term-not-operating">
                  {t('whakahaere.notOperatingLabel', 'Not-operating label')}
                </Field.Label>
                <Controller
                  name="not_operating_label"
                  control={control}
                  render={({ field }) => (
                    <InputField
                      id="term-not-operating"
                      placeholder={DEFAULT_TERMINOLOGY.not_operating_label}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      data-e2e="terminology-not-operating-input"
                    />
                  )}
                />
                {errors.not_operating_label && (
                  <Field.ErrorText>
                    {errors.not_operating_label.message ? t(errors.not_operating_label.message) : null}
                  </Field.ErrorText>
                )}
              </Field.Root>
            </SimpleGrid>

            <Field.Root invalid={!!errors.event_label}>
              <Field.Label htmlFor="term-event">
                {t('whakahaere.scheduledEventLabel', 'Scheduled-event label')}
              </Field.Label>
              <Controller
                name="event_label"
                control={control}
                render={({ field }) => (
                  <InputField
                    id="term-event"
                    placeholder={DEFAULT_TERMINOLOGY.event_label}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    data-e2e="terminology-event-input"
                  />
                )}
              />
              {errors.event_label && (
                <Field.ErrorText>{errors.event_label.message ? t(errors.event_label.message) : null}</Field.ErrorText>
              )}
            </Field.Root>

            <Flex justify="flex-end" pt={2}>
              <ButtonComponent
                visual="primary"
                type="submit"
                disabled={isSubmitting}
                data-e2e="save-terminology-button"
              >
                {own
                  ? t('common.save', 'Save')
                  : scope.schoolId
                  ? t('whakahaere.createSiteOverride', 'Create site override')
                  : t('common.save', 'Save')}
              </ButtonComponent>
            </Flex>
          </VStack>
        </form>
      </VStack>
    </Box>
  );
}

function toForm(
  t: OperatingTerminology | null,
): OperatingTerminologyFormValues {
  return {
    operating_label: t?.operating_label ?? DEFAULT_TERMINOLOGY.operating_label,
    not_operating_label:
      t?.not_operating_label ?? DEFAULT_TERMINOLOGY.not_operating_label,
    event_label: t?.event_label ?? DEFAULT_TERMINOLOGY.event_label ?? '',
  };
}
